import { FC } from "react";
import { Link } from "@mui/material";

export interface ITextSegment {
  type: "text" | "link";
  value: string;
}

// Only explicit `http://` / `https://` URLs are treated as links.
const URL_REGEX = /https?:\/\/[^\s<>"]+/gi;
const TRAILING_PUNCTUATION_REGEX = /[.,;:!?'")\]]+$/;

/**
 * Removes punctuation that most likely belongs to the surrounding sentence
 * rather than to the URL, e.g. `(see https://example.com).`
 * A closing parenthesis is kept if the URL contains a matching opening one.
 */
const splitTrailingPunctuation = (match: string) => {
  let url = match;
  let trailing = "";

  while (TRAILING_PUNCTUATION_REGEX.test(url)) {
    const lastChar = url.slice(-1);

    if (lastChar === ")" && url.includes("(")) break;

    trailing = lastChar + trailing;
    url = url.slice(0, -1);
  }

  return { url, trailing };
};

/**
 * Splits plain text into text and link segments. Only URLs starting with
 * `http://` or `https://` become links; everything else stays verbatim text.
 */
export const splitTextIntoSegments = (text: string): ITextSegment[] => {
  const segments: ITextSegment[] = [];
  let lastIndex = 0;

  const pushText = (value: string) => {
    if (!value) return;

    const previous = segments[segments.length - 1];

    if (previous?.type === "text") {
      previous.value += value;
    } else {
      segments.push({ type: "text", value });
    }
  };

  // eslint-disable-next-line no-restricted-syntax
  for (const match of text.matchAll(URL_REGEX)) {
    const { url, trailing } = splitTrailingPunctuation(match[0]);
    const start = match.index ?? 0;

    pushText(text.slice(lastIndex, start));

    // Scheme without any host (e.g. "https://") is not a link.
    if (/^https?:\/\/./i.test(url)) {
      segments.push({ type: "link", value: url });
      pushText(trailing);
    } else {
      pushText(url + trailing);
    }

    lastIndex = start + match[0].length;
  }

  pushText(text.slice(lastIndex));

  return segments;
};

interface ILinkifiedTextProps {
  text: string;
}

/**
 * Renders text as-is (never as markup) turning `http://` / `https://` URLs
 * into links opened in a new tab.
 */
const LinkifiedText: FC<ILinkifiedTextProps> = ({ text }) => (
  <>
    {splitTextIntoSegments(text).map(({ type, value }, index) =>
      type === "link" ? (
        <Link
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          href={value}
          target="_blank"
          rel="noopener noreferrer"
        >
          {value}
        </Link>
      ) : (
        // eslint-disable-next-line react/no-array-index-key
        <span key={index}>{value}</span>
      )
    )}
  </>
);

export default LinkifiedText;
